import { Document } from '@/interface/chat';
import { useAuth } from '@/hooks/useAuth';
import axiosInstance from '@/api/axiosConfig';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CircularProgress from '@mui/material/CircularProgress';
import React, { useState } from 'react';
import { colors } from '@/theme/colors';
import { useGlobalToast } from '@/providers/globalToastProvider';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@/components/muiComponents';

interface DocumentCardProps {
  document: Document;
  /** When "strip", card is shown in the horizontal document strip (slightly more compact). */
  variant?: 'grid' | 'strip';
}

const DocumentCard: React.FC<DocumentCardProps> = ({
  document,
  variant = 'grid',
}) => {
  const theme = useTheme();
  const { authState } = useAuth();
  const { t } = useTranslation(['chat']);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isViewing, setIsViewing] = useState(false);
  const globalToast = useGlobalToast();

  // Don't render card if documentuuid or originkey is missing
  if (!document.documentuuid || !document.originkey) {
    return null;
  }
  const dataUrlToBlob = (dataUrl: string): Blob => {
    const base64Data = dataUrl.split(',')[1];
    const mimeMatch = dataUrl.match(/data:([^;]+);/);
    const mimeType = mimeMatch ? mimeMatch[1] : 'application/pdf';

    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);

    return new Blob([byteArray], { type: mimeType });
  };

  const fetchDocument = async (): Promise<Blob> => {
    if (!document.documentuuid || !document.originkey) {
      throw new Error('Missing required fields: documentuuid or originkey');
    }

    const response = await axiosInstance.get('/user/fetch-documents', {
      baseURL: process.env.REACT_APP_ORCHESTRATOR_API_URL,
      headers: authState?.idToken?.idToken
        ? {
            Authorization: `Bearer ${authState.idToken.idToken}`,
          }
        : undefined,
      params: {
        documentId: document.documentuuid,
        originKey: document.originkey,
      },
      responseType: 'json',
    });

    const dataUrl = response.data?.data?.dataUrl;
    if (!dataUrl) {
      throw new Error('No dataUrl found in API response');
    }

    return dataUrlToBlob(dataUrl);
  };

  const handleView = async () => {
    setIsViewing(true);
    try {
      const blob = await fetchDocument();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      // Note: URL will be revoked when the window is closed or after a timeout
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
    } catch (error) {
      globalToast.showToast({
        message: t('documents.viewError'),
        severity: 'error',
      });
    } finally {
      setIsViewing(false);
    }
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const blob = await fetchDocument();
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = document.filename || 'document.pdf';
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      globalToast.showToast({
        message: t('documents.downloadError'),
        severity: 'error',
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const formatDate = (dateString?: string): string | null => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return null;
    }
  };

  const dateToDisplay = document.modificationtimestamp;
  const formattedDate = formatDate(dateToDisplay);
  const isStrip = variant === 'strip';

  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: 'column',
        padding: isStrip ? { xs: 1.25, sm: 1.5 } : { xs: 1.5, sm: 2 },
        flex: 1,
        backgroundColor:
          theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(0, 0, 0, 0.02)',
        borderRadius: 1,
        border: `1px solid ${
          theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.1)'
            : 'rgba(0, 0, 0, 0.1)'
        }`,
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
        overflow: 'hidden',
        minHeight: isStrip ? 'auto' : { xs: 'auto', sm: '220px' },
        transition: 'all 0.2s ease',
        boxSizing: 'border-box',
        '&:hover': {
          backgroundColor:
            theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.08)'
              : 'rgba(0, 0, 0, 0.04)',
          transform: 'translateY(-2px)',
          boxShadow:
            theme.palette.mode === 'dark'
              ? '0 4px 12px rgba(0, 0, 0, 0.3)'
              : '0 4px 12px rgba(0, 0, 0, 0.1)',
        },
      }}
    >
      <CardContent
        sx={{
          flex: 1,
          padding: '0',
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: 1, sm: 1.5 },
          minWidth: 0,
          maxWidth: '100%',
          height: '100%',
          overflow: 'hidden',
          boxSizing: 'border-box',
        }}
      >
        {/* Top row: PDF Icon and Document Details */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: { xs: 1, sm: 1.5 },
            alignItems: 'flex-start',
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          {/* PDF Icon at top-left */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: { xs: '40px', sm: '48px' },
              height: { xs: '40px', sm: '48px' },
              minWidth: { xs: '40px', sm: '48px' },
              borderRadius: 1,
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.1)'
                  : 'rgba(0, 0, 0, 0.05)',
              flexShrink: 0,
            }}
          >
            <PictureAsPdfIcon
              sx={{
                color: colors.error,
                fontSize: { xs: '24px', sm: '32px' },
              }}
            />
          </Box>

          {/* Document details next to PDF icon */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              flex: 1,
              minWidth: 0,
            }}
          >
            {/* Filename below icon and details */}
            <Tooltip
              title={document.filename || t('documents.untitledDocument')}
              placement="top"
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: '14px', sm: '16px' },
                  color: theme.palette.text.primary,
                  wordBreak: 'break-word',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                }}
              >
                {document.filename || t('documents.untitledDocument')}
              </Typography>
            </Tooltip>
            <Tooltip
              title={document.documenttype || t('documents.notAvailable')}
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: '12px', sm: '14px' },
                  color: theme.palette.text.secondary,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {t('documents.type')}{' '}
                {document.documenttype || t('documents.notAvailable')}
              </Typography>
            </Tooltip>
            {formattedDate && (
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: '12px', sm: '14px' },
                  color: theme.palette.text.secondary,
                }}
              >
                {t('documents.date')} {formattedDate}
              </Typography>
            )}
            {document.agent?.MemberFullName && (
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: '12px', sm: '14px' },
                  color: theme.palette.text.secondary,
                }}
              >
                {t('documents.associatedAgent')} {document.agent.MemberFullName}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Action buttons: full-width stack on mobile, inline on larger screens */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            flexWrap: 'wrap',
            gap: { xs: 0.75, sm: 1 },
            marginTop: 'auto',
            paddingTop: { xs: 1.5, sm: 2 },
            justifyContent: { xs: 'stretch', sm: 'center' },
            alignItems: 'stretch',
            width: '100%',
            minWidth: 0,
          }}
        >
          <Button
            variant="outlined"
            size="small"
            startIcon={
              isViewing ? <CircularProgress size={16} /> : <VisibilityIcon />
            }
            onClick={handleView}
            sx={{
              flex: { xs: '1 1 auto', sm: '0 0 auto' },
              minWidth: { xs: 'min-content', sm: '120px' },
              textTransform: 'none',
              fontSize: { xs: '12px', sm: '14px' },
              borderRadius: 1,
              whiteSpace: 'nowrap',
            }}
          >
            {isViewing ? t('documents.loading') : t('documents.view')}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={
              isDownloading ? <CircularProgress size={16} /> : <DownloadIcon />
            }
            onClick={handleDownload}
            sx={{
              flex: { xs: '1 1 auto', sm: '0 0 auto' },
              minWidth: { xs: 'min-content', sm: '120px' },
              textTransform: 'none',
              fontSize: { xs: '12px', sm: '14px' },
              borderRadius: 1,
              whiteSpace: 'nowrap',
            }}
          >
            {isDownloading
              ? t('documents.downloading')
              : t('documents.download')}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default DocumentCard;
